import { useTranslation } from '../hooks/useTranslation'
import { amountCurrency } from '../lib/format'
import { groupAddOns } from '../lib/groupAddOns'
import type { CartAddOn, CartItem } from '../types/display'

/** The cashier shows a repeated add-on as "2 x Bacon"; a customer checking
 *  their order should see the same count. */
function addOnLabel(addOn: CartAddOn): string {
  return addOn.quantity > 1 ? `${addOn.quantity} × ${addOn.title}` : addOn.title
}

export default function CartItemRow({ item, currency }: { item: CartItem; currency: string }) {
  const { t, locale } = useTranslation()

  const detailClass = 'text-[clamp(0.65rem,1.2vh,0.75rem)] font-light text-text-mid'
  const groups = groupAddOns(item.addOns)
  const removed = item.removedIngredients ?? []

  return (
    <div className="grid animate-itemIn grid-cols-[1fr_auto_auto] items-center gap-3 px-[clamp(16px,3vw,36px)] py-[clamp(8px,1.5vh,14px)] odd:bg-row-alt">
      <div className="flex flex-col gap-0.5">
        <div className="text-[clamp(0.8rem,1.6vh,0.95rem)] font-medium text-text-hi">
          {item.title}
        </div>

        {groups.map((group, i) => (
          <div key={`${group.categoryTitle ?? 'plain'}-${i}`} className={detailClass}>
            {/* Mirrors the cashier's "Ekstra til pizza: Ekstra ost, Ekstra
                pepperoni". Without a category the add-ons read as a plain list. */}
            {group.categoryTitle && (
              <span className="text-text-lo">{group.categoryTitle}: </span>
            )}
            {group.addOns.map(addOnLabel).join(', ')}
          </div>
        ))}

        {removed.length > 0 && (
          <div className={detailClass}>
            <span className="text-text-lo">{t('without-ingredients')}: </span>
            {removed.join(', ')}
          </div>
        )}
      </div>

      <div className="min-w-[36px] rounded-[20px] bg-divider px-[9px] py-[3px] text-center font-volte text-[clamp(0.65rem,1.2vh,0.78rem)] text-text-mid">
        ×{item.quantity}
      </div>
      <div className="min-w-[72px] text-right font-volte text-[clamp(0.8rem,1.6vh,0.95rem)] text-text-hi">
        {amountCurrency(item.cost, currency, locale)}
      </div>
    </div>
  )
}
